import { createTheme } from '@mui/material/styles';
import { ptBR } from '@mui/material/locale';
import { ptBR as pickersPtBR } from '@mui/x-date-pickers/locales';
import ColorPalette from '../colorpalette/ColorPalette';

const theme = createTheme({
  palette: {
    primary: {
      main: ColorPalette.blueMain,
      light: ColorPalette.blueLight1,
      dark: ColorPalette.blueDark,
    },
    secondary: {
      main: ColorPalette.secondary,
      light: ColorPalette.secondaryLight,
      dark: ColorPalette.secondaryDark || '#D32B70', // Fallback caso não exista
    },
    error: {
      main: ColorPalette.error,
      light: ColorPalette.errorLight,
    },
    warning: {
      main: ColorPalette.warning,
      light: ColorPalette.warningLight,
    },
    info: {
      main: ColorPalette.info,
      light: ColorPalette.infoLight,
    },
    success: {
      main: ColorPalette.success,
      light: ColorPalette.successLight,
    },
    text: {
      primary: ColorPalette.textDark,
      secondary: ColorPalette.textMedium,
      disabled: ColorPalette.textLight,
    },
    background: {
      default: ColorPalette.bgMain,
      paper: ColorPalette.white,
    },
  },
  components: {
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: ColorPalette.blueMain,
            color: ColorPalette.white,
            '&:hover': {
              backgroundColor: ColorPalette.blueDark,
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          height: '40px',
          fontFamily: 'Inter',
          whiteSpace: 'nowrap',
          borderRadius: '8px',
        },
        text: {
          color: ColorPalette.textDark,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: ColorPalette.bgMedium,
          },
        },
        outlined: {
          color: ColorPalette.blueMain,
          borderColor: ColorPalette.blueLight1,
          backgroundColor: 'transparent',
          '&:hover': {
            borderColor: ColorPalette.blueMain,
            backgroundColor: ColorPalette.blueAlpha,
          },
        },
        contained: {
          backgroundColor: ColorPalette.blueMain,
          '&:hover': {
            backgroundColor: ColorPalette.blueDark,
          },
          '&.Mui-disabled': {
            backgroundColor: ColorPalette.bgMedium,
            color: ColorPalette.textLight
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '20px',
          backgroundColor: ColorPalette.bgLight,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: ColorPalette.blueMain,
              boxShadow: `0 0 0 3px ${ColorPalette.blueAlpha}`,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: ColorPalette.bgLight,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: ColorPalette.white,
          '& .MuiTableCell-head': {
            padding: '6px',
            fontWeight: 500,
            color: ColorPalette.textMedium,
          },
          '& .MuiTableCell-head:first-of-type': {
            paddingLeft: '20px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:nth-of-type(odd)': {
            backgroundColor: ColorPalette.white,
          },
          '& .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: ColorPalette.blueLight5, // Azul ultra claro para melhor legibilidade
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px',
          borderBottom: 'none',
        },
        body: {
          color: ColorPalette.textDark,
        },
        head: {
          fontWeight: 500,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td:first-of-type': {
            borderBottomLeftRadius: '8px',
          },
          '&:last-child td:last-of-type': {
            borderBottomRightRadius: '8px',
          },
          '&:first-of-type td:first-of-type': {
            borderTopLeftRadius: '8px',
          },
          '&:first-of-type td:last-of-type': {
            borderTopRightRadius: '8px',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 500,
      letterSpacing: '-0.5px',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.4,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
},
// Textos internos do MUI em português (paginação, "Abrir" do Autocomplete,
// "Mês anterior" dos calendários etc.) para todo o app
ptBR,
pickersPtBR);

export default theme;